/**
 * Narrow browser test seam — only installed when ?testSpeed=1.
 * Invokes the same public production primitive as ArtifactPreview
 * (`exportCreativeArtifact`) — does not reimplement composition.
 */
import {
  deserializeEditGraph,
  serializeEditGraph,
  type EditGraph,
  type SourcePack,
} from "@desk/core";
import { PACK_REGISTRY } from "../../content/registry.js";
import { exportCreativeArtifact } from "./creative-artifact-export.js";

export type TestCaptureRequest = {
  packId?: string;
  pack?: SourcePack;
  graphJson: string;
  /** Exact integer edit tick. */
  tick: number;
  width?: number;
  height?: number;
  scoreText?: string;
  resultCardTheme?: "default" | "portfolio-alt";
};

export type TestCaptureResult =
  | {
      ok: true;
      pngBase64: string;
      width: number;
      height: number;
      fingerprint: string;
      editTick: number;
      sourceTick: number;
      showReconstructionLabel: boolean;
      watermark: string;
      captionText: string;
      transitionKind: string;
      transitionPhase: number;
      stickerCount: number;
    }
  | { ok: false; reason: string };

function readTestSpeedEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("testSpeed") === "1" || params.get("testspeed") === "1";
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

/**
 * Test bridge → canonical `exportCreativeArtifact` only.
 */
export async function captureAtEditTick(
  request: TestCaptureRequest,
): Promise<TestCaptureResult> {
  const pack =
    request.pack ??
    (request.packId
      ? PACK_REGISTRY[request.packId as keyof typeof PACK_REGISTRY]?.pack
      : undefined);
  if (!pack) return { ok: false, reason: "unknown pack" };
  let graph: EditGraph;
  try {
    graph = deserializeEditGraph(request.graphJson);
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof Error ? err.message : "invalid graph",
    };
  }
  const width = request.width ?? 180;
  const height = request.height ?? 320;
  const exported = await exportCreativeArtifact({
    pack,
    editGraph: graph,
    width,
    height,
    tick: request.tick,
    scoreText: request.scoreText ?? "Reach –",
    resultCardTheme: request.resultCardTheme ?? "default",
    outputTickRate: pack.tickRate,
  });
  if (!exported.ok) return { ok: false, reason: exported.reason };
  return {
    ok: true,
    pngBase64: bytesToBase64(exported.data),
    width,
    height,
    fingerprint: exported.sampleFingerprint,
    editTick: exported.editTick,
    sourceTick: exported.sourceTick,
    showReconstructionLabel: exported.showReconstructionLabel,
    watermark: exported.watermark,
    captionText: exported.captionText,
    transitionKind: exported.transitionKind,
    transitionPhase: exported.transitionPhase,
    stickerCount: exported.stickerCount,
  };
}

export type DeskTestCaptureApi = {
  captureAtEditTick: typeof captureAtEditTick;
  serializeGraph: (graph: EditGraph) => string;
  /** Expose production entry for identity checks in-page. */
  exportCreativeArtifact: typeof exportCreativeArtifact;
};

declare global {
  interface Window {
    __DESK_TEST_CAPTURE__?: DeskTestCaptureApi;
    /** testSpeed-only live graph mirror for Playwright. */
    __DESK_EDITOR_GRAPH_JSON__?: string;
  }
}

/** Install only under ?testSpeed=1 — no bridge in normal sessions. */
export function installTestCaptureBridgeIfEnabled(): void {
  if (!readTestSpeedEnabled()) {
    if (typeof window !== "undefined") {
      delete window.__DESK_TEST_CAPTURE__;
    }
    return;
  }
  window.__DESK_TEST_CAPTURE__ = {
    captureAtEditTick,
    serializeGraph: serializeEditGraph,
    exportCreativeArtifact,
  };
}
